/**
 * /admin/audit (BUILD_SPEC.md §5.6, §10.1).
 *
 * Read-only viewer over the AuditLog table. Filters by `action` (free
 * text) and `userId` (typed in directly — the operator typically pulls
 * an id from the users page when investigating one person). Pagination
 * matches the standard 50/page default.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { listAuditLogs } from "@/api/audit";
import type { AuditLog } from "@/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { listUsers } from "@/api/users";

export function AuditAdminPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [userId, setUserId] = useState("");

  const query = useQuery({
    queryKey: ["audit", { page, action, userId }],
    queryFn: () =>
      listAuditLogs({
        page,
        limit: 50,
        ...(action ? { action } : {}),
        ...(userId ? { userId } : {}),
      }),
  });

  // Lightweight user list to populate the filter dropdown so the admin
  // doesn't have to type an opaque id by hand.
  const usersQuery = useQuery({ queryKey: ["users"], queryFn: listUsers });

  const columns: Column<AuditLog>[] = [
    {
      key: "createdAt",
      header: t("audit.when"),
      render: (r) => new Date(r.createdAt).toLocaleString(),
    },
    {
      key: "user",
      header: t("audit.who"),
      render: (r) => {
        const u = usersQuery.data?.find((x) => x.id === r.userId);
        return u ? u.username : r.userId ?? "—";
      },
    },
    {
      key: "action",
      header: t("audit.what"),
      render: (r) => <code className="text-xs">{r.action}</code>,
    },
    {
      key: "target",
      header: t("audit.target"),
      render: (r) =>
        r.targetType ? (
          <span className="text-xs text-muted dark:text-muted-dark">
            {r.targetType}: {r.targetId}
          </span>
        ) : (
          "—"
        ),
      hideOnMobile: true,
    },
    {
      key: "metadata",
      header: t("audit.metadata"),
      render: (r) =>
        r.metadata ? (
          <code className="block max-w-md truncate text-xs text-muted dark:text-muted-dark">
            {JSON.stringify(r.metadata)}
          </code>
        ) : (
          "—"
        ),
      hideOnMobile: true,
    },
    {
      key: "ip",
      header: t("audit.ip"),
      render: (r) => <span className="text-xs">{r.ipAddress ?? "—"}</span>,
      hideOnMobile: true,
    },
  ];

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.limit)) : 1;
  const inputCls =
    "rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-3 py-1.5 text-sm text-fg dark:text-fg-dark";

  return (
    <div className="space-y-3">
      <h2 className="text-xl font-bold text-fg dark:text-fg-dark">{t("admin.audit")}</h2>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder={t("audit.filterAction")}
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
          className={inputCls}
        />
        <select
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            setPage(1);
          }}
          className={inputCls}
        >
          <option value="">{t("audit.anyUser")}</option>
          {usersQuery.data?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.username}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        rows={query.data?.items ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        emptyMessage={t("audit.empty")}
        isLoading={query.isLoading}
      />
    </div>
  );
}
